import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import * as DM from '@radix-ui/react-dropdown-menu';
import cn from 'classnames';
import SVGWrapper, { SVGProps } from '@components/Icons/SVGWrapper';
import { DropdownMenuOption } from '@app/types';
import ExpandingMenu from './ExpandingMenu';
import styles from './Menus.module.scss';
import { useMountedEffect } from '@hooks/use-mounted-effect';
import UdoMenuIcon from '@components/Icons/Udo';
import { PermissionsEditorTriggerButton } from '@components/Modals/ShareResource/ResourcePermissions';
import { ContactOptionsMenuTrigger } from '@views/Contact/Actions/ContactOptionsMenu';
import { useIsOnScreen } from '@support/hooks/use-is-on-screen';

export function EllipsisToXIcon({
  isOpen,
  toggleClassName,
  triggerProps,
  hoverOpacity = true,
  containerWidth = 16,
  containerHeight = 15,
  ...svgProps
}: SVGProps & {
  isOpen: boolean;
  toggleClassName?: string;
  triggerProps?: Record<string, any>;
  containerWidth?: number;
  containerHeight?: number;
}) {
  return (
    <div
      className={cn(styles.toggleIcon, toggleClassName, isOpen && styles.open, isOpen && 'open')}
      style={{ width: containerWidth, height: containerHeight }}
    >
      <SVGWrapper
        viewBox="-1 -1 16 16"
        color="primary"
        {...svgProps}
        hoverOpacity={isOpen ? false : hoverOpacity}
      >
        <circle stroke="none" cx="1.6" cy="7" r="1.9" />
        <circle stroke="none" cx="7" cy="7" r="1.9" />
        <circle stroke="none" cx="12.4" cy="7" r="1.9" />
        <g
          strokeLinecap="round"
          strokeWidth="1.9"
          strokeDasharray="7.5 7.5"
          strokeDashoffset="7.45"
          transform="translate(0.25, 0)"
        >
          <path d="M7 7l5.3 5.3" />
          <path d="M7 7l5.3 -5.3" />
          <path d="M7 7l-5.3 -5.3" />
          <path d="M7 7l-5.3 5.3" />
        </g>
      </SVGWrapper>
    </div>
  );
}

function FallbackTrigger({ isOpen, triggerProps }) {
  if (!!triggerProps.children) {
    return triggerProps.children;
  }

  return <EllipsisToXIcon isOpen={isOpen} triggerProps={triggerProps} />;
}

type TriggerVariant = 'udo' | 'permissions' | 'contactOptions' | 'integrationActions';

interface DropdownMenuProps {
  options: Array<DropdownMenuOption>;
  before?: ReactNode;
  menuClassName?: string;
  triggerClassName?: string;
  triggerProps?: Record<string, any>;
  expandFrom?: 'top-right' | 'bottom-left' | 'top-center' | 'top-left' | 'bottom-right';
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  triggerVariant?: TriggerVariant;
  side?: 'bottom' | 'top';
  suppressFocusControlOnItems?: boolean;
  align?: 'end' | 'start' | 'center';
}

const IntegrationActionsMenuTrigger = ({ isOpen }: { isOpen: boolean }) => (
  <EllipsisToXIcon isOpen={isOpen} containerWidth={12} containerHeight={12} hoverOpacity={false} />
);

// This allows us to pass the isOpen prop (and any arbitrary props passed as triggerProps) to the trigger
// component. Alternatively any trigger component can be passed as a ReactNode as the triggerProps.children.
// We might want to come back to this at some point to standardize the trigger component API.
const triggerForMenu: Record<TriggerVariant, (props: any) => JSX.Element> = {
  udo: UdoMenuIcon,
  permissions: PermissionsEditorTriggerButton,
  contactOptions: ContactOptionsMenuTrigger,
  integrationActions: IntegrationActionsMenuTrigger,
};

export default function DropdownMenu({
  options,
  before = null,
  menuClassName = null,
  triggerClassName = null,
  triggerProps = {},
  expandFrom,
  triggerVariant,
  onOpen,
  onClose,
  disabled,
  side,
  suppressFocusControlOnItems = false,
  align,
}: DropdownMenuProps) {
  const isMounted = useRef(true);
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const Trigger = useMemo(
    () => triggerForMenu[triggerVariant] || FallbackTrigger,
    [triggerVariant],
  );
  const shouldOpenBelow = useIsOnScreen(triggerRef, { rootMargin: '0px 0px -220px 0px' });

  let _expandFrom = expandFrom;
  let _side: 'bottom' | 'top' = 'bottom';

  if (!expandFrom && !side) {
    _expandFrom = 'top-right';
    _side = 'bottom';

    if (!shouldOpenBelow) {
      _expandFrom = 'bottom-right';
      _side = 'top';
    }
  }

  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  }, [isOpen]);

  useMountedEffect(() => {
    if (!isOpen && onClose) {
      onClose();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onOpenChange = (open: boolean) => {
    if (isMounted.current) {
      setIsOpen(open);
    }
  };

  return (
    <DM.Root open={isOpen} onOpenChange={onOpenChange}>
      <DM.Trigger
        className={cn(
          triggerClassName,
          styles.toggle,
          !triggerVariant && !triggerProps?.children && styles.ellipsisTriggerContainer,
        )}
        asChild
        disabled={disabled}
        ref={triggerRef}
      >
        {/* this is wrapped for now so that ref and classname can be properly applied by radix, should figure out how to unwrap! */}
        <div>
          <Trigger isOpen={isOpen} triggerProps={triggerProps} />
        </div>
      </DM.Trigger>
      <ExpandingMenu
        options={options}
        isOpen={isOpen}
        menuClassName={menuClassName}
        expandFrom={_expandFrom}
        before={before}
        side={_side}
        suppressFocusControlOnItems={suppressFocusControlOnItems}
        align={align}
      />
    </DM.Root>
  );
}
